import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import _ from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomPagination from "app/widgets/Shared/Pagination";
import automationServices from "app/services/automations/automation-services";
import { CreateButton, StandardSearchField } from "app/shared/ui";
import AutomationCard from "./components/AutomationCard";
import AutomationGroups, { UNGROUPED_KEY } from "./components/AutomationGroups";
import AutomationsHeader from "./components/AutomationsHeader";
import GroupDialog from "./components/GroupDialog";
import RuleChangeProposals from "./components/RuleChangeProposals";
import AutomationTypePicker from "./components/AutomationTypePicker";
import { UNGROUPED_GROUP_LABEL } from "./components/AutomationMetaHeader";
import { AUTOMATION_TYPES, getAutomationType } from "./types";

const itemKey = (item) => `${item.type}-${item.id}`;

// Lista unificada de Automatizaciones: reglas, controles «Mantener en rango», límites y lo que venga, agrupados por
// carpeta. Las tarjetas se arrastran entre carpetas (o con «Mover a carpeta…» del menú). La página, el tipo, el
// dispositivo y la búsqueda viven en la URL, así «atrás» desde un editor vuelve al mismo lugar.
const AutomationsIndex = ({ setAssistantContext }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page")) || 1;
    const typeFilter = searchParams.get("type") || "";
    const deviceId = searchParams.get("device_id") || "";
    const search = searchParams.get("q") || "";

    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busyKey, setBusyKey] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [menu, setMenu] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [dragging, setDragging] = useState(false);
    // Carpetas creadas en pantalla que todavía no tienen automatizaciones: existen recién cuando reciben la primera.
    const [newGroups, setNewGroups] = useState([]);
    const [groupOptions, setGroupOptions] = useState([]);
    const [moveTarget, setMoveTarget] = useState(null);
    const [newGroupOpen, setNewGroupOpen] = useState(false);
    // Una carga o un cambio puede terminar después de abrir un editor: entonces no se toca el estado.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const updateParams = useCallback((patch) => {
        const next = new URLSearchParams(searchParams);
        _.forEach(patch, (value, key) => {
            if (value === "" || _.isNil(value)) next.delete(key);
            else next.set(key, value);
        });
        if (!_.has(patch, "page")) next.delete("page");
        setSearchParams(next);
    }, [searchParams, setSearchParams]);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        return automationServices.getAutomations(_.omitBy({ page, type: typeFilter, device_id: deviceId, search }, (value) => value === ""))
            .then((response) => {
                if (!mountedRef.current) return;
                setItems(response?.data || []);
                setMeta(response?.meta || null);
            })
            .catch((requestError) => {
                if (!mountedRef.current) return;
                setItems([]);
                setError(requestError?.response?.status === 403
                    ? "No tienes acceso a las automatizaciones de esta empresa."
                    : "No se pudieron cargar las automatizaciones.");
            })
            .finally(() => { if (mountedRef.current) setLoading(false); });
    }, [deviceId, page, search, typeFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        automationServices.getAutomationGroups()
            .then((groups) => { if (mountedRef.current) setGroupOptions(groups); })
            .catch(() => {});
    }, [items]);

    useEffect(() => {
        setAssistantContext?.({ mode: "list", device_id: deviceId || null });
    }, [deviceId, setAssistantContext]);

    const creatableTypes = useMemo(() => {
        const allowed = _.filter(meta?.types, "can_create").map((type) => type.type);
        return _.filter(AUTOMATION_TYPES, (type) => _.includes(allowed, type.key));
    }, [meta]);

    const groups = useMemo(() => {
        const totals = _.keyBy(meta?.groups || [], (group) => group.name ?? "");
        const ordered = [];
        _.forEach(items, (item) => {
            const key = item.group || UNGROUPED_KEY;
            let group = _.find(ordered, { key });
            if (!group) {
                group = { key, name: item.group || UNGROUPED_GROUP_LABEL, total: totals[item.group ?? ""]?.count, items: [] };
                ordered.push(group);
            }
            group.items.push(item);
        });
        const named = _.filter(ordered, (group) => group.key !== UNGROUPED_KEY);
        _.forEach(newGroups, (name) => {
            if (!_.find(named, { key: name })) named.push({ key: name, name, total: 0, items: [] });
        });
        const ungrouped = _.find(ordered, { key: UNGROUPED_KEY })
            || (dragging || newGroups.length ? { key: UNGROUPED_KEY, name: UNGROUPED_GROUP_LABEL, total: 0, items: [] } : null);
        return _.compact([..._.sortBy(named, (group) => group.name.toLowerCase()), ungrouped]);
    }, [dragging, items, meta, newGroups]);

    const canMove = (item) => !!getAutomationType(item.type) && item.can?.update !== false;

    // El cambio se ve al tiro; si el servidor lo rechaza, la lista vuelve a cargarse como estaba.
    const moveToGroup = (item, group) => {
        const target = group?.trim() || null;
        if ((item.group || null) === target) return;
        setItems((current) => current.map((candidate) => (
            itemKey(candidate) === itemKey(item)
                ? { ...candidate, group: target, data: { ...candidate.data, notification_group: target } }
                : candidate
        )));
        automationServices.moveAutomation(item, target)
            .then(() => {
                if (!mountedRef.current) return;
                setNewGroups((current) => current.filter((name) => name !== target));
                load();
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setError("No se pudo mover la automatización.");
                load();
            });
    };

    const createGroup = (name) => {
        setNewGroupOpen(false);
        if (!name || _.some(groups, { key: name })) return;
        setNewGroups((current) => [...current, name]);
    };

    const editorState = { from: `${location.pathname}${location.search}` };
    const openEditor = (item) => navigate(`/automations/rules/${item.type}/${item.id}`, { state: editorState });
    const duplicate = (item) => navigate(`/automations/rules/new/${item.type}?duplicate=${item.id}`, { state: editorState });
    const create = (type) => {
        setPickerOpen(false);
        const query = deviceId ? `?device_id=${deviceId}` : "";
        navigate(`/automations/rules/new/${type.key}${query}`, { state: editorState });
    };

    const startCreate = () => {
        if (creatableTypes.length === 1) create(creatableTypes[0]);
        else setPickerOpen(true);
    };

    const toggleEnabled = (item) => {
        const type = getAutomationType(item.type);
        if (!type) return;
        const enabled = !item.enabled;
        setBusyKey(itemKey(item));
        // Cada tipo responde con su automatización serializada: la tarjeta toma también lo que cambió del lado del servidor
        // (por ejemplo, la salida que se apaga al pausar un control).
        type.setEnabled(item, enabled)
            .then((updated) => setItems((current) => current.map((candidate) => (
                itemKey(candidate) === itemKey(item)
                    ? { ...candidate, enabled, data: { ...candidate.data, ...(updated || {}), enabled } }
                    : candidate
            ))))
            .catch(() => setError("No se pudo cambiar el estado de la automatización."))
            .finally(() => { if (mountedRef.current) setBusyKey(null); });
    };

    const remove = (item) => {
        const type = getAutomationType(item.type);
        if (!type || !window.confirm(type.confirmRemove(item))) return;
        setBusyKey(itemKey(item));
        type.remove(item)
            .then(() => load())
            .catch(() => setError("No se pudo eliminar la automatización."))
            .finally(() => { if (mountedRef.current) setBusyKey(null); });
    };

    const menuType = menu ? getAutomationType(menu.item.type) : null;
    const closeMenu = () => setMenu(null);
    const typeCount = (key) => _.find(meta?.types, { type: key })?.count || 0;
    const totalCount = _.sumBy(meta?.types || [], "count");

    return (
        <>
            <AutomationsHeader
                actions={!!creatableTypes.length && (
                    <CreateButton startIcon={<AddCircleOutlineOutlinedIcon />} onClick={startCreate}>
                        Nueva automatización
                    </CreateButton>
                )}
            />

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                    <Chip
                        label={`Todas${meta ? ` (${totalCount})` : ""}`}
                        color={typeFilter ? "default" : "primary"}
                        variant={typeFilter ? "outlined" : "filled"}
                        onClick={() => updateParams({ type: "" })}
                    />
                    {AUTOMATION_TYPES.map((type) => (
                        <Chip
                            key={`type-filter-${type.key}`}
                            icon={<type.icon fontSize="small" />}
                            label={`${type.label}${meta ? ` (${typeCount(type.key)})` : ""}`}
                            color={typeFilter === type.key ? "primary" : "default"}
                            variant={typeFilter === type.key ? "filled" : "outlined"}
                            onClick={() => updateParams({ type: typeFilter === type.key ? "" : type.key })}
                        />
                    ))}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" startIcon={<CreateNewFolderOutlinedIcon />} onClick={() => setNewGroupOpen(true)} sx={{ textTransform: "none", flexShrink: 0 }}>
                        Nueva carpeta
                    </Button>
                    <StandardSearchField
                        key={`search-${search}`}
                        placeholder="Buscar por nombre"
                        defaultValue={search}
                        onEnter={(value) => updateParams({ q: value.trim() })}
                        sx={{ minWidth: { md: 280 }, flex: 1 }}
                    />
                </Stack>
            </Stack>

            {deviceId && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Chip label={`Filtrando por dispositivo: ${meta?.device?.name || `#${deviceId}`}`} color="primary" variant="outlined" />
                    <Button size="small" onClick={() => updateParams({ device_id: "" })}>Limpiar filtro</Button>
                </Stack>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <RuleChangeProposals onApplied={load} />

            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2}>
                    {!items.length && !error && (
                        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px dashed", borderColor: "divider" }}>
                            <Typography variant="h6" mb={1}>
                                {search || typeFilter || deviceId ? "Ninguna automatización calza con el filtro" : "No hay automatizaciones configuradas"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Crea reglas que avisan o actúan cuando se cumplen condiciones, o controles que mantienen una variable en rango.
                            </Typography>
                        </Paper>
                    )}

                    {(!!items.length || !!newGroups.length) && (
                        <AutomationGroups
                            groups={groups}
                            itemKey={itemKey}
                            canDrag={canMove}
                            onMove={moveToGroup}
                            onDragStart={() => setDragging(true)}
                            onDragEnd={() => setDragging(false)}
                            renderCard={(item) => (
                                <AutomationCard
                                    item={item}
                                    type={getAutomationType(item.type)}
                                    busy={busyKey === itemKey(item)}
                                    expanded={!!expanded[itemKey(item)]}
                                    onToggleExpanded={() => setExpanded((current) => ({ ...current, [itemKey(item)]: !current[itemKey(item)] }))}
                                    onToggleEnabled={toggleEnabled}
                                    onOpenMenu={(event, target) => setMenu({ anchorEl: event.currentTarget, item: target })}
                                />
                            )}
                        />
                    )}

                    {(meta?.total_pages || 1) > 1 && (
                        <CustomPagination
                            currentPage={page}
                            totalPages={meta?.total_pages || 1}
                            onPageSelect={(nextPage) => updateParams({ page: nextPage === 1 ? "" : nextPage })}
                        />
                    )}
                </Stack>
            )}

            <Menu
                anchorEl={menu?.anchorEl}
                open={!!menu}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem
                    disabled={menu?.item?.can?.update === false}
                    onClick={() => { const { item } = menu; closeMenu(); openEditor(item); }}>
                    <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Editar
                </MenuItem>
                <MenuItem
                    disabled={menu?.item?.can?.update === false}
                    onClick={() => { const { item } = menu; closeMenu(); setMoveTarget(item); }}>
                    <DriveFileMoveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Mover a carpeta…
                </MenuItem>
                {menuType?.canDuplicate && (
                    <MenuItem
                        disabled={!_.find(creatableTypes, { key: menuType.key })}
                        onClick={() => { const { item } = menu; closeMenu(); duplicate(item); }}>
                        <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                        Duplicar
                    </MenuItem>
                )}
                <MenuItem
                    sx={{ color: "error.main" }}
                    disabled={menu?.item?.can?.destroy === false}
                    onClick={() => { const { item } = menu; closeMenu(); remove(item); }}>
                    <DeleteOutlineOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Eliminar
                </MenuItem>
            </Menu>

            <AutomationTypePicker open={pickerOpen} types={creatableTypes} onClose={() => setPickerOpen(false)} onSelect={create} />
            <GroupDialog
                open={!!moveTarget}
                title={`Mover «${moveTarget?.name || ""}» a…`}
                initialValue={moveTarget?.group || ""}
                options={_.uniq([...groupOptions, ...newGroups])}
                onClose={() => setMoveTarget(null)}
                onConfirm={(name) => { const target = moveTarget; setMoveTarget(null); if (target) moveToGroup(target, name); }}
            />
            <GroupDialog
                open={newGroupOpen}
                title="Nueva carpeta"
                confirmLabel="Crear"
                allowEmpty={false}
                options={[]}
                onClose={() => setNewGroupOpen(false)}
                onConfirm={createGroup}
            />
        </>
    );
};

export default AutomationsIndex;
